/** This file must only contain pure code and pure imports */

import { type WebGPUEngine } from "../Engines/webgpuEngine.pure";
import { StorageBuffer } from "../Buffers/storageBuffer";
import { ComputeShader } from "../Compute/computeShader.pure";
import { UniformBuffer } from "../Materials/uniformBuffer";
import { type IGPUParticleSystemPlatform } from "./IGPUParticleSystemPlatform";
import { type Buffer, type VertexBuffer } from "../Buffers/buffer.pure";
import { type GPUParticleSystem } from "./gpuParticleSystem.pure";
import { type DataArray, type Nullable } from "../types";
import { type DataBuffer } from "../Buffers/dataBuffer";
import { Constants } from "../Engines/constants";
import { UniformBufferEffectCommonAccessor } from "../Materials/uniformBufferEffectCommonAccessor";
import { type ComputeBindingMapping } from "../Engines/Extensions/engine.computeShader.pure";
import { type Effect } from "../Materials/effect.pure";
import { RegisterClass } from "../Misc/typeStore";

/** @internal */
export class ComputeShaderParticleSystem implements IGPUParticleSystemPlatform {
    private _parent: GPUParticleSystem;
    private _engine: WebGPUEngine;
    private _updateComputeShader: ComputeShader;
    private _simParamsComputeShader: UniformBuffer;
    private _bufferComputeShader: StorageBuffer[] = [];
    private _renderVertexBuffers: Array<{ [key: string]: VertexBuffer }> = [];

    /** @internal */
    public readonly alignDataInBuffer = true;

    /** @internal */
    constructor(parent: GPUParticleSystem, engine: WebGPUEngine) {
        this._parent = parent;
        this._engine = engine;
    }

    /** @internal */
    public contextLost(): void {
        this._updateComputeShader = undefined as any;
        this._bufferComputeShader.length = 0;
        this._renderVertexBuffers.length = 0;
    }

    /** @internal */
    public isUpdateBufferCreated(): boolean {
        return !!this._updateComputeShader;
    }

    /** @internal */
    public isUpdateBufferReady(): boolean {
        return this._updateComputeShader?.isReady() ?? false;
    }

    /** @internal */
    public createUpdateBuffer(defines: string): UniformBufferEffectCommonAccessor {
        const bindingsMapping: ComputeBindingMapping = {
            params: { group: 0, binding: 0 },
            particlesIn: { group: 0, binding: 1 },
            particlesOut: { group: 0, binding: 2 },
            randomTexture: { group: 0, binding: 3 },
            randomTexture2: { group: 0, binding: 4 },
        };
        if (this._parent._sizeGradientsTexture) {
            bindingsMapping["sizeGradientTexture"] = { group: 1, binding: 1 };
        }
        if (this._parent._angularSpeedGradientsTexture) {
            bindingsMapping["angularSpeedGradientTexture"] = { group: 1, binding: 3 };
        }
        if (this._parent._velocityGradientsTexture) {
            bindingsMapping["velocityGradientTexture"] = { group: 1, binding: 5 };
        }
        if (this._parent._limitVelocityGradientsTexture) {
            bindingsMapping["limitVelocityGradientTexture"] = { group: 1, binding: 7 };
        }
        if (this._parent._dragGradientsTexture) {
            bindingsMapping["dragGradientTexture"] = { group: 1, binding: 9 };
        }
        if (this._parent.noiseTexture) {
            bindingsMapping["noiseTexture"] = { group: 1, binding: 11 };
        }
        if (this._parent.flowMap) {
            bindingsMapping["flowMapTexture"] = { group: 1, binding: 13 };
        }
        if (this._parent._meshPositionTexture) {
            bindingsMapping["meshPositionTexture"] = { group: 1, binding: 14 };
        }
        if (this._parent._meshNormalTexture) {
            bindingsMapping["meshNormalTexture"] = { group: 1, binding: 15 };
        }

        this._updateComputeShader = new ComputeShader("updateParticles", this._engine, "gpuUpdateParticles", { bindingsMapping, defines: defines.split("\n") });

        this._simParamsComputeShader?.dispose();
        this._simParamsComputeShader = new UniformBuffer(this._engine, undefined, undefined, "ComputeShaderParticleSystemUBO");

        this._simParamsComputeShader.addUniform("currentCount", 1);
        this._simParamsComputeShader.addUniform("timeDelta", 1);
        this._simParamsComputeShader.addUniform("stopFactor", 1);
        this._simParamsComputeShader.addUniform("randomTextureSize", 1);
        this._simParamsComputeShader.addUniform("lifeTime", 2);
        this._simParamsComputeShader.addUniform("emitPower", 2);
        this._simParamsComputeShader.addUniform("emitIndex", 1);
        this._simParamsComputeShader.addUniform("emitCount", 1);
        if (!this._parent._colorGradientsTexture) {
            this._simParamsComputeShader.addUniform("color1", 4);
            this._simParamsComputeShader.addUniform("color2", 4);
        }
        this._simParamsComputeShader.addUniform("sizeRange", 2);
        this._simParamsComputeShader.addUniform("scaleRange", 4);
        this._simParamsComputeShader.addUniform("angleRange", 4);
        this._simParamsComputeShader.addUniform("gravity", 3);
        if (this._parent._limitVelocityGradientsTexture) {
            this._simParamsComputeShader.addUniform("limitVelocityDamping", 1);
        }
        if (this._parent.isAnimationSheetEnabled) {
            this._simParamsComputeShader.addUniform("cellInfos", 4);
        }
        if (this._parent.noiseTexture) {
            this._simParamsComputeShader.addUniform("noiseStrength", 3);
        }
        if (this._parent.flowMap) {
            this._simParamsComputeShader.addUniform("flowMapProjection", 16);
            this._simParamsComputeShader.addUniform("flowMapStrength", 1);
        }
        if (!this._parent.isLocal) {
            this._simParamsComputeShader.addUniform("emitterWM", 16);
        }
        if (this._parent.attractors.length > 0) {
            this._simParamsComputeShader.addUniform("attractorCount", 1);
            for (let i = 0; i < this._parent.maxAttractors; i++) {
                this._simParamsComputeShader.addUniform("attractorPositionAndStrength[" + i + "]", 4);
            }
        }
        if (this._parent._startSizeGradients && this._parent._startSizeGradients.length > 0) {
            this._simParamsComputeShader.addUniform("startSizeGradientFactor", 1);
        }
        if (this._parent._lifeTimeGradients && this._parent._lifeTimeGradients.length > 0) {
            this._simParamsComputeShader.addUniform("lifeTimeGradientRange", 2);
        }
        if (this._parent._meshPositionTexture) {
            this._simParamsComputeShader.addUniform("meshTriangleCount", 1);
            this._simParamsComputeShader.addUniform("meshTextureWidth", 1);
        }
        if (this._parent.particleEmitterType) {
            this._parent.particleEmitterType.buildUniformLayout(this._simParamsComputeShader);
        }

        this._updateComputeShader.setUniformBuffer("params", this._simParamsComputeShader);

        return new UniformBufferEffectCommonAccessor(this._simParamsComputeShader);
    }

    /** @internal */
    public createVertexBuffers(updateBuffer: Buffer, renderVertexBuffers: { [key: string]: VertexBuffer }): void {
        this._renderVertexBuffers.push(renderVertexBuffers);
    }

    /** @internal */
    public createParticleBuffer(data: number[]): DataArray | DataBuffer {
        const buffer = new StorageBuffer(
            this._engine,
            data.length * 4,
            Constants.BUFFER_CREATIONFLAG_READWRITE | Constants.BUFFER_CREATIONFLAG_VERTEX,
            "ComputeShaderParticleSystemBuffer"
        );

        buffer.update(data);
        this._bufferComputeShader.push(buffer);

        return buffer.getBuffer();
    }

    /** @internal */
    public bindDrawBuffers(index: number, effect: Effect, indexBuffer: Nullable<DataBuffer>): void {
        this._engine.bindBuffers(this._renderVertexBuffers[index], indexBuffer, effect);
    }

    /** @internal */
    public preUpdateParticleBuffer(): void {}

    /** @internal */
    public updateParticleBuffer(index: number, targetBuffer: Buffer, currentActiveCount: number): void {
        this._simParamsComputeShader.update();

        this._updateComputeShader.setTexture("randomTexture", this._parent._randomTexture, false);
        this._updateComputeShader.setTexture("randomTexture2", this._parent._randomTexture2, false);
        if (this._parent._sizeGradientsTexture) {
            this._updateComputeShader.setTexture("sizeGradientTexture", this._parent._sizeGradientsTexture);
        }

        if (this._parent._angularSpeedGradientsTexture) {
            this._updateComputeShader.setTexture("angularSpeedGradientTexture", this._parent._angularSpeedGradientsTexture);
        }

        if (this._parent._velocityGradientsTexture) {
            this._updateComputeShader.setTexture("velocityGradientTexture", this._parent._velocityGradientsTexture);
        }

        if (this._parent._limitVelocityGradientsTexture) {
            this._updateComputeShader.setTexture("limitVelocityGradientTexture", this._parent._limitVelocityGradientsTexture);
        }

        if (this._parent._dragGradientsTexture) {
            this._updateComputeShader.setTexture("dragGradientTexture", this._parent._dragGradientsTexture);
        }

        if (this._parent.noiseTexture) {
            this._updateComputeShader.setTexture("noiseTexture", this._parent.noiseTexture);
        }

        if (this._parent.flowMap) {
            this._updateComputeShader.setTexture("flowMapTexture", this._parent.flowMap);
        }

        if (this._parent._meshPositionTexture) {
            this._updateComputeShader.setTexture("meshPositionTexture", this._parent._meshPositionTexture, false);
        }

        if (this._parent._meshNormalTexture) {
            this._updateComputeShader.setTexture("meshNormalTexture", this._parent._meshNormalTexture, false);
        }

        this._updateComputeShader.setStorageBuffer("particlesIn", this._bufferComputeShader[index]);
        this._updateComputeShader.setStorageBuffer("particlesOut", this._bufferComputeShader[index ^ 1]);

        this._updateComputeShader.dispatch(Math.ceil(currentActiveCount / 64));
    }

    /** @internal */
    public releaseBuffers(): void {
        for (let i = 0; i < this._bufferComputeShader.length; ++i) {
            this._bufferComputeShader[i].dispose();
        }

        this._bufferComputeShader.length = 0;

        this._simParamsComputeShader?.dispose();
        (<any>this._simParamsComputeShader) = null;

        (<any>this._updateComputeShader) = null;
    }

    /** @internal */
    public releaseVertexBuffers(): void {
        this._renderVertexBuffers.length = 0;
    }
}

let _Registered = false;
/**
 * Register side effects for computeShaderParticleSystem.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterComputeShaderParticleSystem(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    RegisterClass("BABYLON.ComputeShaderParticleSystem", ComputeShaderParticleSystem);
}
